import { useEffect } from 'react';

import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// The official Telegram mark (paper-plane glyph), so the connect button reads as
// a genuine Telegram action.
function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

type TelegramStatus = {
  connected: boolean;
  accounts: { username?: string | null; firstName?: string | null }[];
  botUsername: string | null;
  connectUrl: string | null;
};

const statusRequest: RequestSchema<TelegramStatus> = {
  name: 'telegramStatus',
  input: '/api/v1/telegram',
  method: 'GET',
};

const disconnectRequest: RequestSchema<{ revoked: number }> = {
  name: 'telegramDisconnect',
  input: '/api/v1/telegram',
  method: 'DELETE',
};

export default function ConnectTelegram() {
  const status = useRequest(statusRequest, { manual: true });
  const disconnect = useRequest(disconnectRequest, { manual: true });

  // Initial load, and re-check whenever the user returns from the Telegram app
  // (so connecting reflects without a manual refresh).
  useEffect(() => {
    status.run();
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        status.run();
      }
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const data = status.data;
  const connected = !!data?.connected;
  const connectUrl = data?.connectUrl;
  const handles = (data?.accounts ?? [])
    .map((a) => (a.username ? `@${a.username}` : a.firstName))
    .filter(Boolean);

  const onDisconnect = async () => {
    await disconnect.run().catch(() => undefined);
    status.run();
  };

  return (
    <div className="flex flex-col space-y-3">
      <div className="text-sm text-zinc-500 dark:text-zinc-400">
        Get release alerts as a private Telegram message — instant and opt-in. Send{' '}
        <code>/stop</code> to the bot anytime to turn them off.
      </div>

      {connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <span className="font-semibold text-emerald-600 dark:text-emerald-400">Connected</span>
            {handles.length > 0 && <span className="ml-2 text-zinc-500">{handles.join(', ')}</span>}
          </div>
          <div className="flex items-center gap-2">
            {connectUrl && (
              <a
                href={connectUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-md border border-zinc-300 px-3 py-1.5 font-medium text-sm text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                Add another
              </a>
            )}
            <button
              type="button"
              onClick={onDisconnect}
              disabled={disconnect.loading}
              className="rounded-md border border-red-300 px-3 py-1.5 font-medium text-red-700 text-sm hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : connectUrl ? (
        <a
          href={connectUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-2 rounded-md bg-[#229ED9] px-3.5 py-2 font-semibold text-sm text-white transition hover:bg-[#1c8dc2]"
        >
          <TelegramIcon className="size-4" />
          Connect Telegram
        </a>
      ) : (
        <div className="text-sm text-zinc-400 dark:text-zinc-500">
          {status.loading ? 'Loading…' : 'Telegram alerts aren’t available yet.'}
        </div>
      )}
    </div>
  );
}
